import { useCallback, useState } from "react";

import {
  AUTOMATIONS_PANEL,
  CHATS_PANEL,
  INSIGHTS_PANEL,
  LIBRARY_PANEL,
  PROJECTS_PANEL,
} from "src/components/layout/nav/amp-nav-panels";
import { AMP_INITIATIVES_PATH } from "src/components/layout/nav/sections/amp-nav-links";
import { CALENDAR_PATH } from "src/pages/agents/calendar/setup/calendar-ready-notice";

import { useUser } from "src/contexts/user-context";
import { getStoredValue, setStoredValue } from "src/hooks/use-persisted-state";

/** The viewer's arrangement of the AMP nav's root entries, by key. */
export type NavLayout = {
  /** Entries moved under More. A saved key no root entry has is ignored, since layouts outlive entries. */
  hidden: string[];
};

const NAV_LAYOUT_KEY = "amp-nav-layout";

/** A root entry as the layout sees it; one without `hideable` always shows. */
type LayoutEntry = { key: string; hideable?: { isActive: boolean } };

/**
 * Splits the root by the viewer's layout. A hidden entry for the open page stays in the list until the viewer leaves it,
 * and `canHide` is false once one hideable entry is left in the list, so the nav never empties into More.
 */
export function arrangeRootEntries<T extends LayoutEntry>(
  entries: T[],
  layout: NavLayout,
): {
  visible: T[];
  hidden: T[];
  shownWhileActive: Set<string>;
  canHide: boolean;
} {
  const hiddenKeys = new Set(layout.hidden);
  const shownWhileActive = new Set(
    entries.flatMap(({ key, hideable }) =>
      hideable?.isActive && hiddenKeys.has(key) ? [key] : [],
    ),
  );
  const isHidden = ({ key, hideable }: T) =>
    Boolean(hideable) && hiddenKeys.has(key) && !shownWhileActive.has(key);
  const visible = entries.filter((entry) => !isHidden(entry));
  const hidden = entries.filter(isHidden);
  const keptCount = visible.filter(
    ({ key, hideable }) => hideable && !shownWhileActive.has(key),
  ).length;
  return { visible, hidden, shownWhileActive, canHide: keptCount > 1 };
}

/** Hides an entry under More, or brings it back. */
export const toggleHidden = (layout: NavLayout, key: string): NavLayout => ({
  hidden: layout.hidden.includes(key)
    ? layout.hidden.filter((hidden) => hidden !== key)
    : [...layout.hidden, key],
});

/** The viewer's nav layout for the open workspace, kept in this browser. */
export const useNavLayout = () => {
  const { workspace } = useUser();
  const key = workspace ? `${NAV_LAYOUT_KEY}:${workspace.id}` : NAV_LAYOUT_KEY;

  // Read on every render, so switching workspaces picks up that workspace's layout without a reload.
  // Kept in memory too, so hiding still works for the session where storage writes fail.
  const [session, setSession] = useState<{ key: string; layout: NavLayout }>();
  const stored =
    session?.key === key
      ? session.layout
      : getStoredValue<NavLayout | null>(key, null);
  const set = useCallback(
    (layout: NavLayout) => {
      setStoredValue(key, layout);
      setSession({ key, layout });
    },
    [key],
  );
  // A layout stored before a field existed reads as missing it.
  return [{ hidden: stored?.hidden ?? [] }, set] as const;
};

/** One labeled section of the AMP nav's root: its label and its entries' keys, in order. */
export type NavRootSection = { label: string; keys: readonly string[] };

/**
 * The root's sections, top to bottom, under Home. Edit this to move an entry between sections or reorder one;
 * an entry no section lists stays in its own kind's section (`fallbackSection`), after the listed ones.
 */
export const NAV_ROOT_SECTIONS: readonly NavRootSection[] = [
  {
    label: "Agentic Marketing",
    keys: [
      CHATS_PANEL,
      PROJECTS_PANEL,
      LIBRARY_PANEL,
      AUTOMATIONS_PANEL,
      CALENDAR_PATH,
      AMP_INITIATIVES_PATH,
      INSIGHTS_PANEL,
    ],
  },
  {
    label: "Data & Activation",
    keys: [
      "Activation",
      "Customer Studio",
      "AI Decisioning",
      "Native Delivery",
      "Intelligence",
      "Identity Resolution",
      "Integrations",
      "Event Collection",
    ],
  },
];

/**
 * Orders root entries into their sections and labels each with its section. Entries with no `fallbackSection`,
 * like Home, stay on top, unlabeled.
 */
export function placeInSections<
  T extends { key: string; fallbackSection?: number },
>(
  entries: T[],
  sections: readonly NavRootSection[],
): Array<T & { section?: string }> {
  const placed = entries.map((entry, index) => {
    const listed = sections.findIndex(({ keys }) => keys.includes(entry.key));
    const sectionIndex = listed !== -1 ? listed : (entry.fallbackSection ?? -1);
    const keyIndex = sections[sectionIndex]?.keys.indexOf(entry.key) ?? -1;
    return {
      entry,
      index,
      sectionIndex,
      keyIndex: keyIndex === -1 ? Number.MAX_SAFE_INTEGER : keyIndex,
    };
  });
  placed.sort(
    (a, b) =>
      a.sectionIndex - b.sectionIndex ||
      a.keyIndex - b.keyIndex ||
      a.index - b.index,
  );
  return placed.map(({ entry, sectionIndex }) => ({
    ...entry,
    section: sections[sectionIndex]?.label,
  }));
}
